import React, { useEffect, useState } from 'react';
import PlanFeatureMatrix from './PlanFeatureMatrix';

const EMPTY_PLAN = {
  id: '',
  name: '',
  slug: '',
  description: '',
  monthly_price: '0',
  currency: 'cad',
  features: {},
  is_active: true,
  is_trial: false,
  trial_duration_days: 7,
  claim_interval_months: 1
};

const featuresToText = (features = {}) => Object.entries(features)
  .map(([name, description]) => `${name}: ${description}`)
  .join('\n');

const textToFeatures = (text) => Object.fromEntries(
  text.split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separator = line.indexOf(':');
      if (separator < 0) return [line, true];
      return [
        line.slice(0, separator).trim(),
        line.slice(separator + 1).trim() || true
      ];
    })
);

const NEW_PLAN_KEY = '__new_plan__';

const MembershipPlanEditor = ({ plan, plans = [], onSave, onCancel, saving }) => {
  const [form, setForm] = useState(EMPTY_PLAN);
  const [featureMatrix, setFeatureMatrix] = useState({});

  useEffect(() => {
    const nextPlan = { ...EMPTY_PLAN, ...(plan || {}) };
    const matrix = Object.fromEntries(
      plans.map((item) => [
        item.id,
        textToFeatures(featuresToText(item.features || {}))
      ])
    );
    if (!nextPlan.id) {
      matrix[NEW_PLAN_KEY] = textToFeatures(featuresToText(nextPlan.features));
    }
    setForm(nextPlan);
    setFeatureMatrix(matrix);
  }, [plan, plans]);

  const update = (key, value) => setForm((current) => ({
    ...current,
    [key]: value,
    ...(key === 'slug' && value.trim().toLowerCase() === 'free'
      ? { is_active: true }
      : {})
  }));

  const planColumns = [
    ...plans.map((item) => ({ key: item.id, name: item.name })),
    ...(!form.id ? [{ key: NEW_PLAN_KEY, name: form.name || 'New plan' }] : [])
  ];

  const toggleFeature = (planKey, feature, included) => {
    setFeatureMatrix((current) => ({
      ...current,
      [planKey]: {
        ...current[planKey],
        [feature]: included
          ? current[planKey]?.[feature] && String(current[planKey][feature]).toLowerCase() !== 'false'
            ? current[planKey][feature]
            : true
          : false
      }
    }));
  };

  const addFeature = (feature) => {
    setFeatureMatrix((current) => Object.fromEntries(
      Object.entries(current).map(([key, features]) => [
        key,
        { ...features, [feature]: false }
      ])
    ));
  };

  const removeFeature = (feature) => {
    setFeatureMatrix((current) => Object.fromEntries(
      Object.entries(current).map(([key, features]) => {
        const nextFeatures = { ...features };
        delete nextFeatures[feature];
        return [key, nextFeatures];
      })
    ));
  };

  const submit = (event) => {
    event.preventDefault();
    const featurePlans = planColumns.map((item) => ({
      id: item.key,
      name: item.name,
      features: featureMatrix[item.key] || {}
    }));
    const selectedKey = form.id || NEW_PLAN_KEY;

    onSave({
      ...form,
      is_active: form.slug.trim().toLowerCase() === 'free' ? true : form.is_active,
      monthly_price: form.slug.trim().toLowerCase() === 'free' || form.is_trial
        ? '0'
        : form.monthly_price,
      plan_type: form.slug.trim().toLowerCase() === 'free'
        ? 'free'
        : form.is_trial
          ? 'trial'
          : 'paid',
      slug: form.slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      features: featureMatrix[selectedKey] || {},
      featurePlans
    });
  };

  const isFreePlan = form.slug.trim().toLowerCase() === 'free'
    || form.plan_type === 'free';

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-bold text-slate-600">
          Plan name
          <input required value={form.name} onChange={(event) => update('name', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
        </label>
        <label className="text-xs font-bold text-slate-600">
          Plan ID
          <input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={form.slug} onChange={(event) => update('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
        </label>
      </div>
      <label className="block text-xs font-bold text-slate-600">
        Description
        <textarea value={form.description} onChange={(event) => update('description', event.target.value)} rows="2" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
      </label>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="text-xs font-bold text-slate-600">
          Monthly price
          <input required type="number" min="0" step="0.01" value={form.monthly_price} onChange={(event) => update('monthly_price', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
        </label>
        <label className="text-xs font-bold text-slate-600">
          Currency
          <input required minLength="3" maxLength="3" value={form.currency} onChange={(event) => update('currency', event.target.value.toLowerCase())} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm uppercase" />
        </label>
        <label className="flex items-center gap-2 self-end pb-3 text-xs font-bold text-slate-600">
          <input type="checkbox" checked={form.is_trial} onChange={(event) => update('is_trial', event.target.checked)} />
          Claimable trial
        </label>
      </div>
      {form.is_trial && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-xs font-bold text-slate-600">
            Trial duration (days)
            <input required type="number" min="1" max="3650" value={form.trial_duration_days} onChange={(event) => update('trial_duration_days', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-600">
            Claim again after (months)
            <input required type="number" min="1" max="120" value={form.claim_interval_months} onChange={(event) => update('claim_interval_months', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
          </label>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
        <input
          type="checkbox"
          checked={isFreePlan || form.is_active}
          disabled={isFreePlan}
          onChange={(event) => update('is_active', event.target.checked)}
        />
        Available to sellers{isFreePlan ? ' (required for the default Free plan)' : ''}
      </label>
      <div>
        <h4 className="mb-2 text-xs font-bold text-slate-600">Plan features</h4>
        <PlanFeatureMatrix
          plans={planColumns}
          featureMatrix={featureMatrix}
          onToggle={toggleFeature}
          onAddFeature={addFeature}
          onRemoveFeature={removeFeature}
        />
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rounded-xl border border-slate-200 px-5 py-3 text-xs font-black uppercase tracking-wider text-slate-500">Cancel</button>
        <button disabled={saving} type="submit" className="rounded-xl bg-indigo-600 px-5 py-3 text-xs font-black uppercase tracking-wider text-white disabled:opacity-50">{saving ? 'Saving...' : 'Save plan'}</button>
      </div>
    </form>
  );
};

export default MembershipPlanEditor;
